import React, { useState, useLayoutEffect, useEffect, useRef, MouseEvent, Fragment } from 'react';

type OptionProps = {
    option: string;
    value: string;
    disabled?: boolean;
};

type NiceSelectProps = {
    id: string;
    options: OptionProps[];
    selectedIndex?: number;
    label?: string;
    selectClassName?: string;
    selectDefaultValue?: string;
    selectOnLoad?: () => any;
    selectOnChange?: Function;
};

type SelectedOption = {
    option: string;
    value: string;
};

const NiceSelect = ({
    id,
    options,
    selectedIndex,
    label,
    selectClassName,
    selectDefaultValue,
    selectOnLoad,
    selectOnChange,
}: NiceSelectProps) => {
    const [selectedOption, setSelectedOption] = useState<SelectedOption>({
        option: options[0].option,
        value: options[0].value,
    });
    const [isNiceSelectOpen, setIsNiceSelectOpen] = useState<boolean>(false);
    const [isMobileDevice, setIsMobileDevice] = useState<boolean>(true);

    const selectInput = useRef<HTMLSelectElement | null>(null);
    const niceSelectContainer = useRef<HTMLDivElement | null>(null);
    const niceSelectRef = useRef<HTMLButtonElement | null>(null);
    const currentlySelected = useRef<HTMLButtonElement | null>(null);

    const isTouchDevice = () =>
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
            navigator.userAgent,
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            navigator.userAgent.substr(0, 4),
        );
    //const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const setSelectOption = (val: string) => {
        const selected = options.find((opt) => opt.value === val);
        if (selected) {
            const formatSelected = { option: selected.option, value: selected.value };
            setSelectedOption(formatSelected);
        }
    };

    const showHideNiceSelect = (event: MouseEvent<HTMLButtonElement>, isOpen: boolean) => {
        event.preventDefault();
        setIsNiceSelectOpen(isOpen);
        setTimeout(() => currentlySelected.current?.focus(), 10);
    };

    const setNiceSelectOption = (event: MouseEvent<HTMLButtonElement>, selected: SelectedOption) => {
        event.preventDefault();
        setSelectedOption(selected);
        setIsNiceSelectOpen(false);
        niceSelectRef.current?.focus();
    };

    const blurNiceSelect = () => {
        setTimeout(() => {
            if (!niceSelectContainer.current?.contains(document.activeElement)) setIsNiceSelectOpen(false);
        }, 150);
    };

    useLayoutEffect(() => {
        setIsMobileDevice(isTouchDevice);
        if (selectDefaultValue) {
            const defaultValueIndex = options.findIndex((opt) => opt.value === selectDefaultValue);
            setSelectedOption({
                option: options[defaultValueIndex].option,
                value: options[defaultValueIndex].value,
            });
        }
    }, []);

    useEffect(() => {
        if (selectedIndex > -1) {
            setSelectedOption({
                value: options[selectedIndex].value,
                option: options[selectedIndex].option,
            });
        }
    }, [selectedIndex]);

    return (
        <div className="niceSelect">
            <label
                className={`niceSelect__label${isMobileDevice ? '' : ' visually-hidden'}`}
                htmlFor={id}
                tabIndex={!isMobileDevice ? -1 : 0}
                aria-hidden={isMobileDevice}
            >
                {label}
            </label>
            {isMobileDevice && <br />}
            <select
                className={`${selectClassName ? selectClassName : 'niceSelect__select'}${
                    isMobileDevice ? '' : ' visually-hidden'
                }`}
                id={id}
                ref={selectInput}
                value={selectedOption.value}
                onLoad={selectOnLoad}
                onChange={(e) => {
                    setSelectOption(e.currentTarget.value);
                    if (selectOnChange) selectOnChange(e.currentTarget.value);
                }}
                tabIndex={!isMobileDevice ? -1 : 0}
                aria-hidden={isMobileDevice}
            >
                {options.map((opt, i) => {
                    const { option, value, disabled } = opt;
                    return (
                        <option key={`${id}-option-${i}`} value={value} disabled={disabled}>
                            {option}
                        </option>
                    );
                })}
            </select>
            {!isMobileDevice && (
                <div className="niceSelect__container" ref={niceSelectContainer} onBlur={blurNiceSelect}>
                    {label && (
                        <Fragment>
                            <label className="niceSelect__label" id={`niceSelect-${id}-label`}>
                                {label}
                            </label>
                            <br />
                        </Fragment>
                    )}
                    <button
                        className={`niceSelect__selected${isNiceSelectOpen ? ' niceSelect__selected--open' : ''}`}
                        id={`niceSelect-${id}-selected`}
                        ref={niceSelectRef}
                        onClick={(e) => showHideNiceSelect(e, true)}
                        aria-labelledby={`niceSelect-${id}-label niceSelect-${id}-selected`}
                        aria-describedby={`niceSelect-${id}-selected-desc`}
                        aria-haspopup="listbox"
                        aria-controls={`niceSelect-${id}-ul`}
                        aria-expanded={isNiceSelectOpen}
                    >
                        {selectedOption.option}
                    </button>
                    <p
                        className="visually-hidden"
                        id={`niceSelect-${id}-selected-desc`}
                        tabIndex={-1}
                        aria-hidden={true}
                    >
                        Select to choose a different option.
                    </p>
                    {isNiceSelectOpen && (
                        <ul
                            className="niceSelect__options"
                            id={`niceSelect-${id}-ul`}
                            role="listbox"
                            tabIndex={-1}
                            aria-activedescendant={`niceSelect-${id}-option-${selectedOption.value}`}
                        >
                            {options.map((opt, i) => {
                                const { option, value, disabled } = opt;
                                return (
                                    <li key={`niceSelect-${id}-option-${i}`} className="niceSelect__item">
                                        <button
                                            className={`niceSelect__option${
                                                value === selectedOption.value ? ' niceSelect__option--selected' : ''
                                            }`}
                                            id={`niceSelect-${id}-option-${value}`}
                                            role="option"
                                            disabled={disabled}
                                            ref={value === selectedOption.value ? currentlySelected : null}
                                            onClick={(e) => {
                                                setNiceSelectOption(e, { option, value });
                                                if (selectOnChange) selectOnChange(value);
                                            }}
                                            aria-selected={value === selectedOption.value}
                                        >
                                            {option}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default NiceSelect;
